/* globals __DEV__ */
import Host from "enums/Host";
import { azForHost } from "util/envUtils";

/**
 * Domain-based deployment environment definitions.
 * Keys from this object are matched against the end of
 * the domain when config is generated, so 'foo.bar.com'
 * will match 'bar.com'
 *
 * only overrides to defaultConfig.js need to be specified here
 *
 * URI definitions are handled via separate object properties
 * for the relevant parts of the URI, such that
 * 'https://public-api.us-east-1.inindca.com:8000/api/v1'
 * would be represented as:
 *  {
 *      protocol: 'https',
 *      hostname: 'public-api.us-east-1.inindca.com',
 *      port: '8000',
 *      path: '/api/v1',
 *  }
 *
 * The 'port' and 'path' properties are both optional
 */

/**
 * @param  {string} subdomain=''
 * @param  {string} domain=''
 * @returns {string} fully qualified domain
 */
const buildHostname = (subdomain = "", domain = "") =>
    subdomain ? `${subdomain}.${domain}` : domain;

const prodEnv = (host) => {
    return {
        envName: "pca",
        localLogLevel: "info",
        api: {
            protocol: "https",
            hostname: buildHostname("api", host),
            path: "/api/v2",
        },
        uploadService: {
            protocol: "https",
            hostname: buildHostname(`upload.${azForHost(host)}`, host),
            path: "uploads/v2",
        },
    };
};

const APP_DCA = buildHostname("api", Host.DCA);
const APP_TCA = buildHostname("api", Host.TCA);
const UPLOAD_DCA = buildHostname(`upload.${azForHost(Host.DCA)}`, Host.DCA);
const UPLOAD_TCA = buildHostname(`upload.${azForHost(Host.TCA)}`, Host.TCA);

const ENVIRONMENTS = {
    "inindca.com": {
        envName: "dca",
        localLogLevel: "debug",
        api: {
            protocol: "https",
            hostname: APP_DCA,
            path: "/api/v2",
        },
        uploadService: {
            protocol: "https",
            hostname: UPLOAD_DCA,
            path: "uploads/v2",
        },
    },
    "inintca.com": {
        envName: "tca",
        localLogLevel: "debug",
        api: {
            protocol: "https",
            hostname: APP_TCA,
            path: "/api/v2",
        },
        uploadService: {
            protocol: "https",
            hostname: UPLOAD_TCA,
            path: "uploads/v2",
        },
    },

    "mypurecloud.com": prodEnv("mypurecloud.com"),
    "mypurecloud.com.au": prodEnv("mypurecloud.com.au"),
    "mypurecloud.ie": prodEnv("mypurecloud.ie"),
    "mypurecloud.jp": prodEnv("mypurecloud.jp"),
    "mypurecloud.de": prodEnv("mypurecloud.de"),
    "usw2.pure.cloud": {
        // defaults
        ...prodEnv("usw2.pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "usw2.pure.cloud"),
            path: "uploads/v2",
        },
    },
    "apne2.pure.cloud": {
        // defaults
        ...prodEnv("apne2.pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "apne2.pure.cloud"),
            path: "uploads/v2",
        },
    },
    "apne3.pure.cloud": {
        // defaults
        ...prodEnv("apne3.pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "apne3.pure.cloud"),
            path: "uploads/v2",
        },
    },
    "aps1.pure.cloud": {
        // defaults
        ...prodEnv("aps1.pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "aps1.pure.cloud"),
            path: "uploads/v2",
        },
    },
    "apse1.pure.cloud": {
        // defaults
        ...prodEnv("apse1.pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "apse1.pure.cloud"),
            path: "uploads/v2",
        },
    },
    "cac1.pure.cloud": {
        // defaults
        ...prodEnv("cac1.pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "cac1.pure.cloud"),
            path: "uploads/v2",
        },
    },
    "euc2.pure.cloud": {
        // defaults
        ...prodEnv("euc2.pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "euc2.pure.cloud"),
            path: "uploads/v2",
        },
    },
    "euw2.pure.cloud": {
        // defaults
        ...prodEnv("euw2.pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "euw2.pure.cloud"),
            path: "uploads/v2",
        },
    },
    "mec1.pure.cloud": {
        // defaults
        ...prodEnv("mec1.pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "mec1.pure.cloud"),
            path: "uploads/v2",
        },
    },
    "mxc1.pure.cloud": {
        // defaults
        ...prodEnv("mxc1.pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "mxc1.pure.cloud"),
            path: "uploads/v2",
        },
    },
    "sae1.pure.cloud": {
        // defaults
        ...prodEnv("sae1.pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "sae1.pure.cloud"),
            path: "uploads/v2",
        },
    },
    "use2.us-gov-pure.cloud": {
        // defaults
        ...prodEnv("use2.us-gov-pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "use2.us-gov-pure.cloud"),
            path: "uploads/v2",
        },
    },
    "edee1.eusc-pure.cloud": {
        // defaults
        ...prodEnv("edee1.eusc-pure.cloud"),
        // overrides
        uploadService: {
            protocol: "https",
            hostname: buildHostname("app", "edee1.eusc-pure.cloud"),
            path: "uploads/v2",
        },
    },
};

if (__DEV__) {
    ENVIRONMENTS[".*"] = {
        envName: "dca",
        embeddingOrigin: "*",
        localLogLevel: "debug",
        serverLogLevel: "none",
        api: {
            hostname: APP_DCA,
            path: "/api/v2",
            protocol: "https",
        },
        uploadService: {
            protocol: "https",
            hostname: UPLOAD_DCA,
            path: "/uploads/v2",
        },
    };
}

export default ENVIRONMENTS;
